import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ClientInteractionsView from "./ClientInteractionsView";
import ClientInteractionModal from "./ClientInteractionModal";
import { clientInteractionsService } from "../../../../services/clientInteractions.service";
import { clientsService } from "../../../../services/clients.service";

export default function ClientInteractionsPage({ authData, logout }) {
  const { t, i18n } = useTranslation();
  void i18n;
  const [loading, setLoading] = useState(false);

  const [clients, setClients] = useState([]);
  const [interactions, setInteractions] = useState([]);

  const [clientIdFilter, setClientIdFilter] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState(null);

  // ✅ Pagination (client-side)
  const PAGE_SIZE = 5;
  const [page, setPage] = useState(1); // 1-based

  const unwrap = (res) => {
    if (res && typeof res === "object" && "data" in res) return res.data;
    return res;
  };

  const loadClients = async () => {
    const res = await clientsService.list({});
    const list = unwrap(res);
    setClients(Array.isArray(list) ? list : []);
  };

  const loadInteractions = async (clientId = clientIdFilter) => {
    const res = await clientInteractionsService.list({
      clientId: clientId?.trim() ? clientId.trim() : undefined,
    });
    const list = unwrap(res);
    const arr = Array.isArray(list) ? list : [];
    setInteractions(arr);

    // ✅ عند أي تحميل/فلترة نرجع لأول صفحة
    setPage(1);
  };

  const refreshAll = async () => {
    setLoading(true);
    try {
      await Promise.all([loadClients(), loadInteractions()]);
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return;
      }
      alert(e?.message || t("sales.clientInteractions.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setOpenModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setEditing(null);
  };

  const createInteraction = async (payload) => {
    setLoading(true);
    try {
      await clientInteractionsService.create(payload);
      await loadInteractions();
      return true;
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return false;
      }
      alert(e?.message || t("sales.clientInteractions.createFailed"));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateInteraction = async (id, payload) => {
    setLoading(true);
    try {
      await clientInteractionsService.update(id, payload);
      await loadInteractions();
      return true;
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return false;
      }
      alert(e?.message || t("sales.clientInteractions.updateFailed"));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteInteraction = async (id) => {
    const ok = window.confirm(t("sales.clientInteractions.confirmDelete"));
    if (!ok) return;

    setLoading(true);
    try {
      await clientInteractionsService.remove(id);
      await loadInteractions();
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return;
      }
      alert(e?.message || t("sales.clientInteractions.deleteFailed"));
    } finally {
      setLoading(false);
    }
  };

  const clientMap = useMemo(() => {
    const m = new Map();
    (clients || []).forEach((c) => m.set(String(c.id), c));
    return m;
  }, [clients]);

  // ✅ Pagination derived
  const totalCount = Array.isArray(interactions) ? interactions.length : 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), totalPages);

  const pagedInteractions = useMemo(() => {
    const arr = Array.isArray(interactions) ? interactions : [];
    const start = (safePage - 1) * PAGE_SIZE;
    return arr.slice(start, start + PAGE_SIZE);
  }, [interactions, safePage]);

  const goToPage = (p) => {
    const next = Math.min(Math.max(1, p), totalPages);
    setPage(next);
  };

  return (
    <>
      <ClientInteractionsView
        authData={authData}
        logout={logout}
        loading={loading}
        clients={clients}
        interactions={pagedInteractions}     // ✅ page items only
        totalCount={totalCount}             // ✅ total (all pages)
        page={safePage}
        pageSize={PAGE_SIZE}
        totalPages={totalPages}
        onPageChange={goToPage}
        clientMap={clientMap}
        clientIdFilter={clientIdFilter}
        setClientIdFilter={setClientIdFilter}
        onApplyFilter={async () => {
          setLoading(true);
          try {
            await loadInteractions(clientIdFilter);
          } catch (e) {
            if (e?.message === "UNAUTHORIZED") {
              logout?.();
              return;
            }
            alert(e?.message || t("sales.clientInteractions.filterFailed"));
          } finally {
            setLoading(false);
          }
        }}
        onClearFilter={async () => {
          setClientIdFilter("");
          setLoading(true);
          try {
            await loadInteractions("");
          } catch (e) {
            if (e?.message === "UNAUTHORIZED") {
              logout?.();
              return;
            }
            alert(e?.message || t("sales.clientInteractions.clearFailed"));
          } finally {
            setLoading(false);
          }
        }}
        onRefresh={refreshAll}
        onCreateClick={openCreate}
        onEdit={openEdit}
        onDelete={deleteInteraction}
      />

      {openModal && (
        <ClientInteractionModal
          initialData={editing}
          clients={clients}
          loadingClients={loading && (!clients || clients.length === 0)}
          onClose={closeModal}
          onSubmit={async (payload) => {
            if (editing?.id) {
              const ok = await updateInteraction(editing.id, payload);
              if (ok) closeModal();
            } else {
              const ok = await createInteraction(payload);
              if (ok) closeModal();
            }
          }}
        />
      )}
    </>
  );
}
